import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

export default function AdminProtectedRoute({ children }) {
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    loginWithRedirect,
    getAccessTokenSilently,
  } = useAuth0();

  const [isCheckingAdmin, setIsCheckingAdmin] = React.useState(true);
  const [isPlatformAdmin, setIsPlatformAdmin] = React.useState(false);
  const [checkFailed, setCheckFailed] = React.useState(false);

  React.useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (!isAuthenticated) {
      setIsCheckingAdmin(false);

      loginWithRedirect({
        appState: {
          returnTo: window.location.pathname,
        },
      });

      return;
    }

    let cancelled = false;

    async function checkAdminAccess() {
      try {
        setIsCheckingAdmin(true);
        setCheckFailed(false);

        const accessToken = await getAccessTokenSilently();

        const response = await fetch(`${API_BASE_URL}/api/me/`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Could not verify administrator access (${response.status}).`,
          );
        }

        const data = await response.json();

        if (cancelled) {
          return;
        }

        const hasAdminAccess =
          data?.linked === true && data?.is_platform_admin === true;

        setIsPlatformAdmin(hasAdminAccess);
      } catch (error) {
        console.error("Failed to verify Platform Admin access:", error);

        if (!cancelled) {
          setIsPlatformAdmin(false);
          setCheckFailed(true);
        }
      } finally {
        if (!cancelled) {
          setIsCheckingAdmin(false);
        }
      }
    }

    checkAdminAccess();

    return () => {
      cancelled = true;
    };
  }, [
    isAuthLoading,
    isAuthenticated,
    loginWithRedirect,
    getAccessTokenSilently,
  ]);

  if (isAuthLoading || (isAuthenticated && isCheckingAdmin)) {
    return <div style={{ padding: 24 }}>Loading...</div>;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (checkFailed) {
    return (
      <div style={{ padding: 24 }}>Could not verify administrator access.</div>
    );
  }

  if (!isPlatformAdmin) {
    return <Navigate to="/post-login" replace />;
  }

  return children;
}
